import type { Dispatch, SetStateAction } from "react"
import React, { Fragment, useEffect, useState } from "react"

import { Trans, useTranslation } from "react-i18next"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useDispatch } from "react-redux"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"

import AddIcon from "@mui/icons-material/Add"
import CloseIcon from "@mui/icons-material/Close"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import {
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  useMediaQuery,
  useTheme as useMuiTheme,
} from "@mui/material"
import {
  Button,
  Menu,
  MenuItem,
  NotificationUtils,
  Skeleton,
  Tag,
  Tooltip,
  Typography,
} from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import moment from "moment"

import { KonanAvatarComponent } from "../../../components/core/KonanAvatar"
import { BaseSimpleDialog } from "../../../components/dialogs/BaseSimpleDialog"
import { ModelType } from "../../../components/status/ModelType"
import { getTheme } from "../../../hooks/UseTheme"
import { KonanAPI } from "../../../services/KonanAPI"
import { queryClient } from "../../../store/QueryClientStore"
import type { Model } from "../../../types/generated/api/Model"
import { setCurrentModel } from "../../Models"
import { AssignTrainingDataToModelDialog } from "./AssignTrainingDataToModelDialog"

import styles from "../TrainingData.module.scss"

type ParamType = {
  id: string
}

type DialogProps = {
  open: boolean
  onDialogClose: () => void
  model_uuid: string
  model_name: string | undefined
}

function UnlinkTrainingDataFromModelDialog(props: Readonly<DialogProps>): React.ReactElement {
  const { t } = useTranslation("TrainingData")
  const { open, onDialogClose, model_uuid, model_name } = props
  const muiTheme = useMuiTheme()

  const unlinkModelMutation = useMutation<AxiosResponse, AxiosError>(() =>
    KonanAPI.deleteTrainingDataFromModel(model_uuid),
  )

  async function unlinkModel(): Promise<void> {
    try {
      await unlinkModelMutation.mutateAsync()
      await queryClient.invalidateQueries(["model", model_uuid])
      setTimeout(() => unlinkModelMutation.reset(), 300)

      onDialogClose()
      NotificationUtils.toast(t("training_data_card.notifications.unlinkSuccess", { modelName: model_name }), {
        severity: "success",
      })
    } catch (e) {
      NotificationUtils.toast(t("training_data_card.notifications.unlinkError", { modelName: model_name }), {
        severity: "error",
      })

      onDialogClose()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if ((reason === "backdropClick" || reason === "escapeKeyDown") && unlinkModelMutation.isLoading) {
          return undefined
        } else {
          onDialogClose()
        }
      }}
      fullScreen={useMediaQuery(muiTheme.breakpoints.down("md"))}
    >
      <DialogTitle className="dialog-header-base">
        <Typography variant="h2-bold">{t("dialogs.unlinkModel.title")}</Typography>
      </DialogTitle>
      <DialogContent className="dialog-content-base">
        <Typography variant="h3-regular" gutterBottom>
          <Trans
            i18nKey="dialogs.unlinkModel.message"
            values={{ modelName: model_name, modelUuid: model_uuid }}
            components={{ bold: <strong /> }}
            ns="TrainingData"
          />
        </Typography>
      </DialogContent>
      <DialogActions className="dialog-actions-base">
        <Button variant="secondary" onClick={onDialogClose}>
          {t("dialogs.unlinkModel.cancel")}
        </Button>
        <Button
          variant="dangerous"
          onClick={() => unlinkModel()}
          disabled={unlinkModelMutation?.isLoading}
          isLoading={unlinkModelMutation?.isLoading}
        >
          {t("dialogs.unlinkModel.unlink")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

type trainingProps = {
  modelId: string
  setShadowModel: Dispatch<SetStateAction<string | undefined>>
}

function MiniTrainingDataCard(props: Readonly<trainingProps>): React.ReactElement {
  const { modelId, setShadowModel } = props
  const { id: projectId } = useParams<ParamType>()

  const theme = getTheme()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [unlinkModelDialogOpen, setUnlinkModelDialogOpen] = useState<boolean>(false)

  const {
    isLoading: isModelDataLoading,
    isError,
    data: modelData,
  } = useQuery<AxiosResponse<Model>, AxiosError>(["model", modelId], () => KonanAPI.fetchModel(modelId), {
    enabled: !!modelId,
  })

  const handleUnlinkModelDialogClose = (): void => {
    setUnlinkModelDialogOpen(false)
    queryClient.invalidateQueries(["training-data", projectId])
  }

  useEffect(() => {
    if (isError) {
      setShadowModel(modelId)
    } else {
      setShadowModel("")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, modelData, modelId])

  return (
    <Fragment>
      <UnlinkTrainingDataFromModelDialog
        model_uuid={modelId}
        open={unlinkModelDialogOpen}
        onDialogClose={handleUnlinkModelDialogClose}
        model_name={modelData?.data?.name}
      />
      {isModelDataLoading ? (
        <Card className="card-box-shadow">
          <Grid container direction="column" className={"MiniModelCard"}>
            <Grid item sx={{ marginTop: "8px" }}>
              <Skeleton variant="rectangular" width={"40%"} height={10} />
            </Grid>
            <Grid item>
              <Skeleton variant="rectangular" width={"70%"} height={10} />
            </Grid>
            <Grid item style={{ marginBottom: "5px" }}>
              <Skeleton variant="rectangular" width={"40%"} height={10} />
            </Grid>
          </Grid>
        </Card>
      ) : (
        <Card className="card-box-shadow" style={{ borderRadius: "4px" }}>
          <Grid
            container
            direction="column"
            justifyContent="flex-start"
            alignItems="flex-start"
            className={styles.dataCard}
          >
            <Grid item xs={12} direction="row" justifyContent="space-between" container>
              <Grid item xs={10}>
                <ModelType modelState={modelData?.data?.state} size="xsmall" />
              </Grid>
              <Grid item xs={2} container justifyContent="flex-end" alignItems="flex-start">
                <IconButton
                  style={{ color: theme.palette.gray.background[1], marginRight: "-5px" }}
                  size="small"
                  onClick={() => setUnlinkModelDialogOpen(true)}
                >
                  <CloseIcon fontSize="small" className={styles.remove} />
                </IconButton>
              </Grid>
            </Grid>
            <Grid item xs={12} sx={{ width: "100%" }} marginTop={0.25}>
              <Typography
                variant="p"
                noWrap
                className={styles.trainingText}
                color="important"
                variantColor={2}
                onClick={() => {
                  navigate(`/projects/${projectId}/decision-engines`)
                  dispatch(setCurrentModel(modelData?.data))
                }}
              >
                {modelData?.data?.name}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {modelData?.data?.created_at && (
                <Tooltip
                  title={moment(modelData?.data?.created_at).format("DD/MM/YYYY, h:mm A")}
                  placement="right"
                  width="100%"
                >
                  <Typography variant="label-regular" noWrap>
                    {moment(modelData?.data?.created_at).fromNow()}
                  </Typography>
                </Tooltip>
              )}
            </Grid>
          </Grid>
        </Card>
      )}
    </Fragment>
  )
}

type Props = {
  createdAt: string
  createdBy: string
  name: string
  models: string[]
  trainingDataId: string
  uuid: string
  status: "success" | "error" | "pending" | "processing"
  isValid: boolean
  isLoading: boolean
}

export function TrainingDataCard(props: Readonly<Props>): React.ReactElement {
  const { t } = useTranslation("TrainingData")
  const { createdAt, createdBy, name, models, trainingDataId, uuid, status } = props
  const { id: projectId } = useParams<ParamType>()

  const { pathname, search } = useLocation()

  const muiTheme = useMuiTheme()
  const theme = getTheme()
  const smallerScreens = useMediaQuery(muiTheme.breakpoints.down("sm"))

  const DEFAULT_TRAINING_DATA_NAME = t("training_data_card.defaultFileName")

  const [shadowModel, setShadowModel] = useState<string | undefined>(undefined)
  const [deleteTrainingDataDialogOpen, setDeleteTrainingDataDialogOpen] = useState<boolean>(false)
  const [assignModelDialogOpen, setAssignModelDialogOpen] = useState<boolean>(false)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [allModels, setAllModels] = useState([...models])

  const deleteTrainingDataMutation = useMutation<AxiosResponse, AxiosError>(
    () => KonanAPI.deleteTrainingDataFromProject(projectId as string, trainingDataId),
    {
      onSuccess: async () => {
        // Invalidate react-query queries
        await queryClient.invalidateQueries(["training-data", projectId])

        const displayName = name || t("notifications.defaultTrainingName")
        NotificationUtils.toast(t("training_data_card.notifications.deleteSuccess", { trainingName: displayName }), {
          severity: "success",
        })
      },
      onError: () => {
        const displayName = name || t("notifications.defaultTrainingName")
        NotificationUtils.toast(t("training_data_card.notifications.deleteError", { trainingName: displayName }), {
          severity: "error",
        })
      },
    },
  )

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  /**TEMPORARY FIX
   *
   * this problem of displaying TRAINING JOB in the training data is currently an issue returning
   * when fetching /training-data endpoint from the backend, it shouldn't return the id of the shadow model
   * so this effect hides the shadow model if it exists
   */
  useEffect(() => {
    if (shadowModel?.length) {
      const assignedModels = [...models]
      assignedModels?.splice(models?.indexOf(shadowModel), models?.indexOf(shadowModel) + 1)
      setAllModels([...assignedModels])
    } else {
      setAllModels([...models])
    }
  }, [shadowModel, models])

  return (
    <Fragment>
      {assignModelDialogOpen && (
        <AssignTrainingDataToModelDialog
          projectId={projectId as string}
          open={assignModelDialogOpen}
          onClose={() => setAssignModelDialogOpen(false)}
          trainingDataId={trainingDataId}
          trainingName={name}
        />
      )}
      {deleteTrainingDataDialogOpen && (
        <BaseSimpleDialog
          open={deleteTrainingDataDialogOpen}
          isLoading={deleteTrainingDataMutation.isLoading}
          name={name}
          onAccept={() => deleteTrainingDataMutation.mutateAsync()}
          onClose={() => setDeleteTrainingDataDialogOpen(false)}
          mode={"training-data-deletion"}
        />
      )}
      <Card className="card-box-shadow">
        <Grid container direction="column" className={styles.trainingCardDataHeaderRoot}>
          <Grid item xs={12} container justifyContent="space-between" alignItems="flex-start">
            <Grid item xs={8} alignItems="center" justifyContent="flex-start" className={styles.tag}>
              <Tag color="green" size="small">
                {t("training_data_card.status.success")}
              </Tag>
            </Grid>
            <Grid item xs={2} container justifyContent="flex-end" alignItems="flex-start">
              <IconButton aria-label="settings" onClick={handleClick} size="small" className={styles.headerButton}>
                <MoreHorizIcon />
              </IconButton>
              <Menu id="simple-menu" anchorEl={anchorEl} menuMaxContent open={Boolean(anchorEl)} onClose={handleClose}>
                <MenuItem
                  onClick={() => {
                    setAssignModelDialogOpen(true)
                    handleClose()
                  }}
                >
                  <Typography variant="a">{t("training_data_card.menu.assignToModel")}</Typography>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setDeleteTrainingDataDialogOpen(true)
                    handleClose()
                  }}
                >
                  <Typography className={styles.menuItemDelete} variant="a">
                    {t("training_data_card.menu.deleteTrainingData")}
                  </Typography>
                </MenuItem>
              </Menu>
            </Grid>
          </Grid>
          {status === "success" ? (
            <Link to={`${pathname}${search}&file_id=${uuid}`} className={styles.titleLink}>
              <Typography variant="h3-bold" noWrap className={styles.headerName} color="important" variantColor={2}>
                {name ?? DEFAULT_TRAINING_DATA_NAME}
              </Typography>
            </Link>
          ) : (
            <Typography variant="h3-bold" noWrap className={styles.headerName}>
              {name ?? DEFAULT_TRAINING_DATA_NAME}
            </Typography>
          )}

          <Grid item container xs={12} justifyContent="space-between">
            <Grid
              container
              item
              spacing={1}
              xs={allModels?.length !== 0 ? 6 : 12}
              xl={8}
              alignItems="flex-start"
              className={styles.trainingDataAvatarContainer}
            >
              <KonanAvatarComponent createdBy={createdBy} title={createdBy} createdAt={createdAt} />
            </Grid>
            {shadowModel !== undefined && allModels?.length !== 0 && (
              <Grid item container xs={6} xl={4} className={styles.assignContainer}>
                <Button onClick={() => setAssignModelDialogOpen(true)} size={"small"} variant="secondary">
                  {smallerScreens
                    ? t("training_data_card.actions.assign")
                    : t("training_data_card.actions.assignToModel")}
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid container direction="column" className={styles.bodyRoot}>
          <Grid container item direction="row" alignItems="flex-end" marginBottom={1}>
            <Typography variant="label-bold">{t("labels.assignedModels")}</Typography>
          </Grid>
          {allModels?.length > 0 ? (
            <Grid container direction="row" spacing={1} className={styles.horizontalScroller}>
              {allModels?.map((modelID) => (
                <Grid item xs={4} className={styles.containerCard} key={modelID}>
                  <MiniTrainingDataCard modelId={modelID} setShadowModel={setShadowModel} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid
              item
              py={4}
              xs={12}
              className={styles.addTrainingSection}
              onClick={() => setAssignModelDialogOpen(true)}
            >
              <AddIcon style={{ color: theme.palette.gray.background[1] }} />
              <Typography variant="h3-bold">{t("training_data_card.actions.assignToModelShort")}</Typography>
            </Grid>
          )}
        </Grid>
      </Card>
    </Fragment>
  )
}
